import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, ShieldCheck, KeyRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  // 2FA state — stays on the same page, just swaps the form body.
  const [stage, setStage] = useState("password"); // "password" | "otp" | "backup"
  const [otp, setOtp] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  async function attempt(extras = {}) {
    setBusy(true);
    const r = await login(email, password, extras);
    setBusy(false);
    if (r.twofa_required) {
      setStage("otp");
      toast.info("Two-factor required — enter the 6-digit code from your authenticator.");
      return;
    }
    if (!r.ok) {
      // On a bad OTP the user stays in the OTP stage — clear the field so they can retry.
      if (stage !== "password") { setOtp(""); setBackupCode(""); }
      toast.error(r.error || "Login failed");
      return;
    }
    toast.success(`Welcome back, ${r.user.name || r.user.email}`);
    navigate(r.user.role === "admin" ? "/admin" : "/portal", { replace: true });
  }

  function submitPassword(e) {
    e.preventDefault();
    attempt();
  }

  function submitOtp(e) {
    e.preventDefault();
    if (otp.length !== 6) { toast.error("Enter the full 6-digit code"); return; }
    attempt({ totp_code: otp });
  }

  function submitBackup(e) {
    e.preventDefault();
    if (!backupCode.trim()) { toast.error("Paste a backup code"); return; }
    attempt({ backup_code: backupCode.trim() });
  }

  return (
    <div className="min-h-screen bg-slate-950 grid lg:grid-cols-5">
      {/* left: form */}
      <div className="lg:col-span-3 flex flex-col p-8 lg:p-16">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-50 transition-colors w-fit" data-testid="back-to-landing">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <div className="flex-1 flex items-center max-w-md w-full mx-auto">
          <div className="w-full">
            {stage === "password" && (
              <>
                <div className="mb-10">
                  <h1 className="font-display text-4xl tracking-tight font-medium mb-2">Sign in</h1>
                  <p className="text-slate-400">Admins and partners use the same door.</p>
                </div>

                <form onSubmit={submitPassword} className="space-y-5" data-testid="login-form">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">Email</label>
                    <input
                      data-testid="login-email"
                      type="email"
                      required
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full rounded-lg border border-slate-800 bg-slate-900 px-4 py-3 text-slate-50 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-colors"
                      placeholder="you@company.com"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 block">Password</label>
                    <input
                      data-testid="login-password"
                      type="password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-lg border border-slate-800 bg-slate-900 px-4 py-3 text-slate-50 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-colors"
                      placeholder="••••••••"
                    />
                  </div>
                  <button
                    data-testid="login-submit"
                    type="submit"
                    disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 bg-brand-gradient text-slate-950 disabled:opacity-60 px-6 py-3.5 rounded-full font-semibold text-sm transition-all hover:-translate-y-[1px] hover:brightness-110 hover:shadow-[0_10px_40px_-8px_rgba(40,208,255,0.5)]"
                  >
                    {busy ? "Signing in…" : "Sign in"} {!busy && <ArrowRight className="w-4 h-4" />}
                  </button>
                </form>

                <div className="mt-8 text-sm text-slate-400">
                  <Link to="/forgot-password" data-testid="login-forgot-password-link" className="text-slate-400 hover:text-cyan-300 font-medium">
                    Forgot password?
                  </Link>
                  <span className="mx-2 text-slate-700">·</span>
                  New here?{" "}
                  <Link to="/register" data-testid="login-to-register" className="text-cyan-400 hover:text-cyan-300 font-medium">
                    Create a tenant account
                  </Link>
                </div>
              </>
            )}

            {stage === "otp" && (
              <>
                <div className="mb-10">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-300 text-xs font-semibold uppercase tracking-wider mb-4">
                    <ShieldCheck className="w-3.5 h-3.5" /> Two-factor
                  </div>
                  <h1 className="font-display text-4xl tracking-tight font-medium mb-2">Enter your code</h1>
                  <p className="text-slate-400">
                    Open your authenticator and type the 6-digit code for <span className="text-slate-200 font-mono">{email}</span>.
                  </p>
                </div>

                <form onSubmit={submitOtp} className="space-y-6" data-testid="login-otp-form">
                  <div className="flex justify-center py-2">
                    <InputOTP
                      maxLength={6}
                      value={otp}
                      onChange={setOtp}
                      autoFocus
                      data-testid="login-otp-input"
                    >
                      <InputOTPGroup className="gap-2">
                        {[0,1,2,3,4,5].map((i) => (
                          <InputOTPSlot
                            key={i}
                            index={i}
                            className="h-14 w-12 text-xl bg-slate-900 border-slate-800 text-slate-50"
                          />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>

                  <button
                    data-testid="login-otp-submit"
                    type="submit"
                    disabled={busy || otp.length !== 6}
                    className="w-full inline-flex items-center justify-center gap-2 bg-brand-gradient text-slate-950 disabled:opacity-60 px-6 py-3.5 rounded-full font-semibold text-sm transition-all hover:-translate-y-[1px] hover:brightness-110"
                  >
                    {busy ? "Verifying…" : "Verify & continue"} {!busy && <ArrowRight className="w-4 h-4" />}
                  </button>
                </form>

                <div className="mt-8 text-sm text-slate-400 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => { setStage("password"); setOtp(""); }}
                    data-testid="login-back-to-password"
                    className="inline-flex items-center gap-1.5 hover:text-slate-200"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" /> Use a different account
                  </button>
                  <button
                    type="button"
                    onClick={() => setStage("backup")}
                    data-testid="login-use-backup-code"
                    className="inline-flex items-center gap-1.5 text-cyan-400 hover:text-cyan-300 font-medium"
                  >
                    <KeyRound className="w-3.5 h-3.5" /> Use a backup code
                  </button>
                </div>
              </>
            )}

            {stage === "backup" && (
              <>
                <div className="mb-10">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-300 text-xs font-semibold uppercase tracking-wider mb-4">
                    <KeyRound className="w-3.5 h-3.5" /> Backup code
                  </div>
                  <h1 className="font-display text-4xl tracking-tight font-medium mb-2">Use a backup code</h1>
                  <p className="text-slate-400">
                    Paste one of the 8-character codes you saved when you turned on 2FA. Each code works once.
                  </p>
                </div>

                <form onSubmit={submitBackup} className="space-y-5" data-testid="login-backup-form">
                  <input
                    data-testid="login-backup-input"
                    type="text"
                    required
                    autoFocus
                    autoComplete="off"
                    value={backupCode}
                    onChange={(e) => setBackupCode(e.target.value)}
                    className="w-full rounded-lg border border-slate-800 bg-slate-900 px-4 py-3 text-slate-50 font-mono outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-colors tracking-widest"
                    placeholder="xxxx-xxxx"
                  />
                  <button
                    data-testid="login-backup-submit"
                    type="submit"
                    disabled={busy}
                    className="w-full inline-flex items-center justify-center gap-2 bg-brand-gradient text-slate-950 disabled:opacity-60 px-6 py-3.5 rounded-full font-semibold text-sm transition-all hover:-translate-y-[1px] hover:brightness-110"
                  >
                    {busy ? "Verifying…" : "Verify & continue"} {!busy && <ArrowRight className="w-4 h-4" />}
                  </button>
                </form>

                <div className="mt-8 text-sm text-slate-400">
                  <button
                    type="button"
                    onClick={() => { setStage("otp"); setBackupCode(""); }}
                    data-testid="login-back-to-otp"
                    className="inline-flex items-center gap-1.5 hover:text-slate-200"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to authenticator code
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* right: visual */}
      <div className="hidden lg:block lg:col-span-2 relative overflow-hidden bg-[#040814]">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-transparent to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex flex-col items-start">
            <img src="/brand/steele-cloud-long.png" alt="Steele Cloud" className="w-full max-w-[320px] mb-10 -ml-2" />
            <h2 className="font-display text-3xl tracking-tight font-medium leading-tight">
              Every payment.<br/>Every partner.<br/>Every month.
            </h2>
            <p className="text-slate-400 mt-4 max-w-sm text-sm leading-relaxed">
              Cloud Hosting · NBN · VoIP · PBX Systems. Your wholesale referrals — tracked, calculated, and paid.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
